import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Field, FieldRenderProps } from 'react-final-form';
import { Box, Stack, Typography } from '@mui/material';
import * as maptilersdk from '@maptiler/sdk';
import '@maptiler/sdk/dist/maptiler-sdk.css';

const MAPTILER_API_KEY = import.meta.env.VITE_MAPTILER_API_KEY;
interface MapLocationFieldProps {
  name: string;
  label?: string;
  required?: boolean;
  initialLocation?: {
    latitude: string | number;
    longitude: string | number;
  };
}

interface MapLocationFieldState {
  latitude: string;
  longitude: string;
}

interface MapMarker {
  lng: number;
  lat: number;
  marker?: maptilersdk.Marker;
}

const MapLocationFieldComponent: FC<
  FieldRenderProps<MapLocationFieldState, HTMLElement>
> = ({ input, meta }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maptilersdk.Map | null>(null);
  const [marker, setMarker] = useState<MapMarker | null>(null);
  const currentMarkerRef = useRef<maptilersdk.Marker | null>(null);

  const onChangeRef = useRef(input.onChange);
  const inputValueRef = useRef(input.value);

  useEffect(() => {
    onChangeRef.current = input.onChange;
    inputValueRef.current = input.value;
  }, [input.onChange, input.value]);

  const clearAllMarkers = useCallback(() => {
    if (currentMarkerRef.current) {
      currentMarkerRef.current.remove();
      currentMarkerRef.current = null;
    }

    setMarker(null);
  }, []);

  useEffect(() => {
    maptilersdk.config.apiKey = MAPTILER_API_KEY;

    if (map.current || !mapContainer.current) {
      return;
    }

    const defaultCenter: [number, number] = [0, 0];
    const defaultZoom = 1;

    map.current = new maptilersdk.Map({
      container: mapContainer.current,
      center: defaultCenter,
      zoom: defaultZoom,
    });

    const mapInstance = map.current;

    mapInstance.on('load', () => {
      const inputValue = inputValueRef.current;

      const hasLocation =
        inputValue &&
        typeof inputValue === 'object' &&
        'latitude' in inputValue &&
        'longitude' in inputValue &&
        inputValue.latitude &&
        inputValue.longitude;

      if (hasLocation) {
        const lng = parseFloat(String(inputValue.longitude));
        const lat = parseFloat(String(inputValue.latitude));

        if (!isNaN(lng) && !isNaN(lat)) {
          addMarker(lng, lat);

          mapInstance.flyTo({
            center: [lng, lat],
            zoom: 5,
            essential: true,
          });
        }
      }
    });

    mapInstance.on('click', (e) => {
      const lngLat = e.lngLat;

      clearAllMarkers();

      addMarker(lngLat.lng, lngLat.lat);

      const inputValue = inputValueRef.current;

      const existingValue: Partial<MapLocationFieldState> =
        typeof inputValue === 'object' && inputValue !== null
          ? {
              latitude:
                typeof inputValue.latitude === 'string' ? inputValue.latitude : '',
              longitude:
                typeof inputValue.longitude === 'string' ? inputValue.longitude : '',
            }
          : { latitude: '', longitude: '' };

      const updatedValue: MapLocationFieldState = {
        ...existingValue,
        latitude: lngLat.lat.toString(),
        longitude: lngLat.lng.toString(),
      };

      onChangeRef.current(updatedValue);
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearAllMarkers]);

  const addMarker = useCallback((lng: number, lat: number) => {
    if (!map.current) {
      return;
    }

    if (currentMarkerRef.current) {
      currentMarkerRef.current.remove();
      currentMarkerRef.current = null;
    }

    const markerElement = document.createElement('div');
    markerElement.className = 'marker';
    markerElement.style.width = '25px';
    markerElement.style.height = '25px';
    markerElement.style.borderRadius = '50%';
    markerElement.style.backgroundColor = '#4caf50'; // Always use green for the marker
    markerElement.style.border = '2px solid white';

    const newMarker = new maptilersdk.Marker({
      element: markerElement,
      anchor: 'bottom',
    })
      .setLngLat([lng, lat])
      .addTo(map.current);

    if (map.current) {
      currentMarkerRef.current = newMarker;
      setMarker({ lng, lat, marker: newMarker });
    }
  }, []);

  const hasError = Boolean(meta.error && (meta.touched || meta.submitFailed));
  const errorMessage = hasError ? String(meta.error) : undefined;

  return (
    <Stack spacing={2}>
      <Box
        ref={mapContainer}
        sx={{
          height: '400px',
          width: '100%',
          borderRadius: '8px',
          border: hasError ? '1px solid #d32f2f' : '1px solid #ccc',
        }}
      />
      {marker && (
        <Stack direction="row" spacing={2}>
          <Typography sx={{ color: 'principal.grey70' }}>
            Latitude: {marker.lat.toFixed(6)}
          </Typography>
          <Typography sx={{ color: 'principal.grey70' }}>
            Longitude: {marker.lng.toFixed(6)}
          </Typography>
        </Stack>
      )}
      {hasError && errorMessage && (
        <Typography color="error" variant="captionSingle">
          {errorMessage}
        </Typography>
      )}
    </Stack>
  );
};

export const MapLocationField: FC<MapLocationFieldProps> = ({
  name,
  label,
  required,
  initialLocation,
}) => {
  const initialValue = initialLocation
    ? {
        latitude: String(initialLocation.latitude),
        longitude: String(initialLocation.longitude),
      }
    : undefined;

  return (
    <Stack spacing={1}>
      {label && <Typography sx={{ color: 'principal.grey70' }}>{label}</Typography>}
      <Field<MapLocationFieldState, HTMLElement>
        name={name}
        render={MapLocationFieldComponent}
        initialValue={initialValue}
        required={required}
      />
    </Stack>
  );
};
